/* eslint-disable */
/* eslint-disable jsx-control-statements/jsx-jcs-no-undef */
import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Tooltip,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { useAppData } from '../../hooks/useAppData';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import Image from '../../components/DynamicImages';
import SimpleDialog from '../../components/Dialog';
import { generatePDF, isDealer } from '../../utils';
import ViewStove from '../ViewStove/ViewStove';
import { useTheme } from '@emotion/react';
import { ScreenRotation } from '@mui/icons-material';
import useDealer from '../../hooks/useDealer';
import html2pdf from 'html2pdf.js';
// import { styles as classes } from './CreateKitchen.styles';

const CreateKitchen = () => {
  const { appDataState, fetchInitialData, configuredKitchenData, reset } =
    useAppData();
  const [showCountryPopup, setCountryPopup] = useState(false);
  const [showMobilePopup, setMobilePopup] = useState(false);
  const [save, setSave] = useState(false);
  const [kitchen, setKitchen] = useState({
    cabinetSrc: '',
    flooringSrc: '',
    appliancesSrc: [],
    patchSrc: '',
  });
  const navigate = useNavigate();
  const params = useParams();

  const { parentPath, dreamKitchen } = useDealer();

  // Breakpoints for PDF Generation
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const tablet = useMediaQuery(theme.breakpoints.up('sm'));
  const mobile = useMediaQuery(theme.breakpoints.up('xs'));

  const onMobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const sizes = () => {
    if (desktop) return [-140, 0];
    if (tablet) return [-140, 0];
    if (mobile) return [-110, 0];
  };

  // END

  // clear old local storage entries based on version that will execute only once
  const localStorageVersion = JSON.parse(
    localStorage.getItem(parentPath),
  )?.version;

  if (localStorageVersion !== process.env.REACT_APP_VERSION) {
    localStorage.removeItem(parentPath);
  }

  useEffect(() => {
    fetchInitialData(parentPath);
  }, [fetchInitialData, parentPath]);

  // Country Popup
  useEffect(() => {
    const country = sessionStorage.getItem('country');
    if (country !== 'USA' && country !== 'CANADA') {
      setCountryPopup(true);
    }
  }, []);

  // Mobile Popup; if breakpoint is for mobile
  useEffect(() => {
    if (onMobileView) {
      setMobilePopup(true);
    }
  }, [onMobileView]);

  const handleClose = () => {
    return false;
  };

  const handleDialog = (countryName) => {
    sessionStorage.setItem('country', countryName);
    setCountryPopup(false);
  };

  const dialogBody = (
    <>
      <DialogTitle id="alert-dialog-title">
        <Box className="TextCenter">Please select your location</Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Box className="TextCenter">
            <b>Select</b> your location below. <br></br>
            Then click the <b>+</b> on the appliance you want to work on first.
            This will take you to Step 2, the <b>"Create"</b> page.
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box className="ContentCenter">
          <Button variant="contained" onClick={() => handleDialog('USA')}>
            USA
          </Button>
          <Button variant="contained" onClick={() => handleDialog('CANADA')}>
            CANADA
          </Button>
        </Box>
      </DialogActions>
    </>
  );

  // END

  const { data, configuredData, kitchenData } = appDataState;

  // Room-scene type
  const isStove = data?.type === 'stove';

  // Reset configuredData on kitchen change
  useEffect(() => {
    if (parentPath) {
      reset();
    }
  }, [parentPath]);

  // useEffect(() => {
  //   if (isStove) {
  //      navigate('range/product-studio');
  //   }
  // }, [location?.pathname, isStove]);

  const fetchEnvSrc = (configuredData) => {
    if (Object.keys(configuredData)?.length > 0 && dreamKitchen) {
      let envArray = ['30', '30'];
      for (const item of data?.appliances) {
        const { env, envOrder } = configuredData?.[item.name]?.style || {};
        envArray[envOrder] = env;
      }
      envArray = envArray.filter((i) => i != undefined);

      const env = envArray[0] + '_' + envArray[1];
      // console.log('env', env);
      return { env };
    }
  };

  useEffect(() => {
    const { env } = fetchEnvSrc(configuredData) || {};

    if (kitchenData && dreamKitchen) {
      const { cabinetColour, flooringStyle } = kitchenData;

      const defaultCabinet = data?.cabinetColour?.find((item) => item.default);
      const defaultFlooring = data?.flooringStyle?.find((item) => item.default);

      setKitchen((prevState) => ({
        ...prevState,
        cabinetSrc:
          cabinetColour?.['src_' + env] ||
          cabinetColour?.src ||
          defaultCabinet?.['src_' + env] ||
          defaultCabinet?.src,
        patchSrc:
          cabinetColour?.['patch_' + env] ||
          cabinetColour?.patch ||
          defaultCabinet?.['patch_' + env] ||
          defaultCabinet?.patch,
        flooringSrc:
          flooringStyle?.['src_' + env] ||
          flooringStyle?.src ||
          defaultFlooring?.['src_' + env] ||
          defaultFlooring?.src,
      }));
    }
  }, [data, kitchenData, configuredData, dreamKitchen]);

  useEffect(() => {
    const defaultCabinet = data?.cabinetColour?.find((item) => item.default);

    if (isStove || dreamKitchen) {
      return;
    }

    if (defaultCabinet) {
      setKitchen((prevState) => ({
        ...prevState,
        cabinetSrc: defaultCabinet?.src,
        patchSrc: defaultCabinet?.patch,
      }));
    } else {
      setKitchen((prevState) => ({
        ...prevState,
        cabinetSrc: data?.cabinetColour?.[0]?.src,
        patchSrc: data?.cabinetColour?.[0]?.patch,
      }));
    }
  }, [data, configuredData, setKitchen, isStove]);

  useEffect(() => {
    const defaultFlooring = data?.flooringStyle?.find((item) => item.default);

    if (isStove || dreamKitchen) {
      return;
    }

    if (defaultFlooring) {
      setKitchen((prevState) => ({
        ...prevState,
        flooringSrc: defaultFlooring?.src,
      }));
    } else {
      setKitchen((prevState) => ({
        ...prevState,
        flooringSrc: data?.flooringStyle?.[0]?.src,
      }));
    }
  }, [data, configuredData, setKitchen, isStove]);

  useEffect(() => {
    let defaultAppliances = [];
    let env_pos = '';

    if (Object.keys(configuredData)?.length > 0 && dreamKitchen) {
      defaultAppliances = data?.appliances?.map((item) => {
        if (!item?.hide) {
          env_pos = fetchEnvSrc(configuredData)?.env;
          return {
            src: configuredData?.[item.name]?.imagesSet || [
              item.defaultModelSrc,
            ],
            positioning: item?.[env_pos] || item.positioning,
          };
        }
      });
    } else {
      defaultAppliances = data?.appliances?.map((item) => {
        if (!item?.hide) {
          return {
            src: [item.defaultModelSrc],
            positioning: item?.[env_pos] || item.positioning,
          };
        }
      });
    }

    setKitchen((prevState) => ({
      ...prevState,
      appliancesSrc: defaultAppliances,
    }));
  }, [data?.appliances, configuredData, setKitchen]);

  const cabinetColour = () => {
    return (
      <ul>
        {data?.cabinetColour?.map((item) => {
          const { env } = fetchEnvSrc(configuredData) || {};
          return (
            <li key={item?.name}>
              <Tooltip placement="top" arrow title={item?.name || ''}>
                <a
                  className={
                    (kitchen.cabinetSrc === item?.['src_' + env] ||
                      kitchen.cabinetSrc === item?.src) &&
                    `Active`
                  }
                  style={{
                    backgroundImage: `url(
                    ${require(`../../assets/${item?.icon}`)}
                  )`,
                  }}
                  onClick={() => {
                    setKitchen({
                      ...kitchen,
                      cabinetSrc: item?.['src_' + env] || item?.src,
                      patchSrc: item?.['patch_' + env] || item?.patch,
                    });
                    configuredKitchenData({
                      cabinetColour: item,
                    });
                  }}
                >
                  <span>{item?.name || ''}</span>
                </a>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    );
  };

  const flooringStyle = () => {
    return (
      <ul>
        {data?.flooringStyle?.map((item) => {
          const { env } = fetchEnvSrc(configuredData) || {};
          return (
            <li key={item?.name}>
              <Tooltip placement="top" arrow title={item?.name || ''}>
                <a
                  className={
                    (kitchen.flooringSrc === item?.['src_' + env] ||
                      kitchen.flooringSrc === item?.src) &&
                    `Active`
                  }
                  style={{
                    backgroundImage: `url(
                    ${require(`../../assets/${item?.icon}`)}
                  )`,
                  }}
                  onClick={() => {
                    setKitchen({
                      ...kitchen,
                      flooringSrc: item?.['src_' + env] || item?.src,
                    });
                    configuredKitchenData({
                      flooringStyle: item,
                    });
                  }}
                >
                  <span>{item?.name || ''}</span>
                </a>
              </Tooltip>
            </li>
          );
        })}
      </ul>
    );
  };

  const configuratorIcon = () =>
    data?.configuratorIcon?.map((item) => (
      <Box
        key={item.positioning}
        component="span"
        className={`CommonIconBox ${item.positioning}`}
        onClick={() => navigate(`./${item.id}/product-studio`)}
      >
        <Tooltip placement="top-start" arrow title={item.title}>
          <Box className="Icon"></Box>
        </Tooltip>
      </Box>
    ));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadConfiguredImage = (configuredAppliance) => {
    const { src, positioning } = configuredAppliance || {};

    return (
      <>
        {src?.length > 0 &&
          src?.map((el) => (
            <Image
              key={el}
              classes="childImageBox"
              style={{
                left: positioning?.[0],
                top: positioning?.[1],
              }}
              name={el}
            />
          ))}
      </>
    );
  };

  // scroll to top

  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 140);
  }, [params.appliances]);

  return (
    <div id="dreamKitchen">
      <SimpleDialog
        open={showMobilePopup}
        handleClose={handleClose}
        dialogBody={
          <>
            <DialogContent>
              <Box mb={1} className="TextCenter">
                <ScreenRotation className="TextCenter" />
              </Box>
              <DialogContentText id="alert-dialog-description">
                <Box className="TextCenter">
                  Please rotate your device to landscape for the best
                  experience.
                </Box>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={() => setMobilePopup(false)}>
                Ok
              </Button>
            </DialogActions>
          </>
        }
      />
      {!params.appliances && (
        <div id="cK">
          {!isStove ? (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              maxWidth="lg"
              marginLeft="auto"
              marginRight="auto"
              className="VisualizerMainContainer"
            >
              <Grid item xs={12}>
                <Box className="VisualizerHeading">
                  <Typography variant="h1" textAlign="center">
                    {!dreamKitchen
                      ? 'LET’S CREATE YOUR DREAM KITCHEN!'
                      : 'YOUR ELMIRA DREAM KITCHEN'}
                  </Typography>
                  <Typography variant="h6" textAlign="center">
                    {`${data?.name?.toUpperCase()} COLLECTION`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box className="createKitchenSection">
                  <Box
                    className="HeaderSection no-print"
                    data-html2canvas-ignore="true"
                  >
                    <Box className="HeaderLeftSide">
                      {dreamKitchen && (
                        <>
                          <Button
                            variant="Button"
                            className="SavePdfBtn"
                            disabled={save}
                            onClick={() => {
                              setSave(true);
                              generatePDF(parentPath, '', sizes, setSave);
                            }}
                          >
                            {!save ? 'SAVE AS PDF' : 'Saving...'}
                          </Button>
                          <Button
                            variant="Button"
                            onClick={() => {
                              reset();
                              localStorage.removeItem(parentPath);
                              navigate(`../${parentPath}`);
                            }}
                            className="ResetBtn"
                          >
                            RESET
                          </Button>
                        </>
                      )}
                    </Box>

                    <Box className="HeaderRightSide">
                      <Box className="CabinetColorSection">
                        <Typography variant="h6">CABINET COLOUR</Typography>
                        <Box className="CabinetColors CommonCircle">
                          {cabinetColour()}
                        </Box>
                      </Box>
                      <Box className="FlooringStyleSection">
                        <Typography variant="h6">Flooring Style</Typography>
                        <Box className="CabinetColors CommonCircle">
                          {flooringStyle()}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box id="MainKitchenSection" className="MainKitchenSection">
                    <Box className="MainKitchenInnerWrapper">
                      <Box className="parentKitchenImageBox width100">
                        {kitchen.cabinetSrc && (
                          <Image
                            classes="firstChildImageBox width100"
                            name={kitchen.cabinetSrc}
                          />
                        )}
                        {kitchen.flooringSrc && (
                          <Image
                            classes="childImageBox width100"
                            name={kitchen.flooringSrc}
                          />
                        )}
                        {kitchen?.appliancesSrc?.length > 0 &&
                          kitchen?.appliancesSrc?.map((configuredAppliance) =>
                            loadConfiguredImage(configuredAppliance),
                          )}
                        {kitchen.patchSrc && (
                          <Image
                            classes="childImageBox"
                            name={kitchen.patchSrc}
                          />
                        )}
                      </Box>
                      {!dreamKitchen && configuratorIcon()}
                      {!dreamKitchen && (
                        <Box
                          component="span"
                          className="CommonIconBoxLast IconLast"
                        >
                          {/* <Image name="images/VisualizerIcon.png" /> */}
                          <Box component="div" className="CustomizeBox">
                            {/* CUSTOMIZE YOUR {parentPath} APPLIANCE */}
                            Click the + on the appliance you want to work on
                            first.
                            <Box className="CustomizeBoxMobile">
                              Scroll horizontally to View All Appliances
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box
                    className="FooterSection page-break no-print"
                    data-html2canvas-ignore="true"
                  >
                    {parentPath !== 'heritage' ? (
                      <Typography variant="body1" textAlign="center">
                        <b>PLEASE NOTE:</b> Kitchen and product images are for
                        illustrative purposes only, not for installation or
                        cabinet planning. View appliance specification sheets
                        for exact dimensions of products.
                      </Typography>
                    ) : (
                      <Typography variant="body1" textAlign="center">
                        <b>PLEASE NOTE:</b> Kitchen and product images are for
                        illustrative purposes only, not for installation or
                        cabinet planning. View appliance specification sheets
                        for exact dimensions of products. <br /> Dishwasher will
                        not display if added but will remain with your selection
                        below.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <ViewStove kitchen={kitchen} />
          )}
          {!dreamKitchen && (
            <SimpleDialog
              open={showCountryPopup}
              handleClose={handleClose}
              dialogBody={dialogBody}
            />
          )}
        </div>
      )}
      <Outlet />
    </div>
  );
};

export default CreateKitchen;
